import { useCallback, useMemo } from 'react';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  shouldHidePolicySetting,
  type PolicySettingVisibilityFlags,
} from './policySettingsVisibility';

const VISIBILITY_FLAG_KEYS = [
  flagsmithFeatureFlags.EXTENSION_CODE_SCAN,
  flagsmithFeatureFlags.HOOK_PREVENTION_MODE,
  flagsmithFeatureFlags.AI_MCP_TOOL_ANALYSIS,
  flagsmithFeatureFlags.MCP_TOOL_RESPONSE_SCANNING,
  flagsmithFeatureFlags.CAPABILITY_DETECTION,
  flagsmithFeatureFlags.AI_TOOL_CALL,
];

export interface PolicySettingVisibility {
  flags: PolicySettingVisibilityFlags;
  shouldHideSetting: (settingKey: string) => boolean;
}

/**
 * Feature-flag-gated row visibility for a policy's settings, shared by the
 * Policies page form and the alert "Edit Policy" popup (KIR-4929) so the two
 * hide exactly the same rows.
 */
export function usePolicySettingVisibility(
  policyKey: string
): PolicySettingVisibility {
  const { flags: raw } = useFeatureFlags(VISIBILITY_FLAG_KEYS);
  const isExtensionCodeScanEnabled =
    raw[flagsmithFeatureFlags.EXTENSION_CODE_SCAN] ?? false;
  const isHookPreventionModeEnabled =
    raw[flagsmithFeatureFlags.HOOK_PREVENTION_MODE] ?? false;
  const isAiMcpToolAnalysisEnabled =
    raw[flagsmithFeatureFlags.AI_MCP_TOOL_ANALYSIS] ?? false;
  const isMcpToolResponseScanningEnabled =
    raw[flagsmithFeatureFlags.MCP_TOOL_RESPONSE_SCANNING] ?? false;
  const isCapabilityDetectionEnabled =
    raw[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  const isAiToolCallEnabled = raw[flagsmithFeatureFlags.AI_TOOL_CALL] ?? false;

  const flags = useMemo<PolicySettingVisibilityFlags>(
    () => ({
      isExtensionCodeScanEnabled,
      isHookPreventionModeEnabled,
      isAiMcpToolAnalysisEnabled,
      isMcpToolResponseScanningEnabled,
      isCapabilityDetectionEnabled,
      isAiToolCallEnabled,
    }),
    [
      isExtensionCodeScanEnabled,
      isHookPreventionModeEnabled,
      isAiMcpToolAnalysisEnabled,
      isMcpToolResponseScanningEnabled,
      isCapabilityDetectionEnabled,
      isAiToolCallEnabled,
    ]
  );

  const shouldHideSetting = useCallback(
    (settingKey: string): boolean =>
      shouldHidePolicySetting(settingKey, policyKey, flags),
    [policyKey, flags]
  );

  return { flags, shouldHideSetting };
}
